import React, {useState} from "react";
import {makeStyles} from "@mui/styles";
import {
    FormControl,
    MenuItem,
    Select,
    Button,
    Box,
    Tabs, Tab,
    Grid, OutlinedInput, Input, DialogTitle, Typography, DialogContent, DialogActions, Dialog, Alert, CircularProgress,
    Backdrop
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EventForm from "./EventForm";
import InputLabel from "@mui/material/InputLabel";
import {AddLink} from "@mui/icons-material";
import {useAuth} from "../utils/useAuth";
import {authorizedFetch} from "../utils/authorizedFetch";
import {CustomTabPanel} from "../utils/customTabPanel";
import LooksForm from "./LooksForm";
import Snackbar from "@mui/material/Snackbar";

const useStyles = makeStyles(() => ({
    root: {},
    paper: {
        padding: 16,
        display: "grid",
        margin: 15,
        width: "95%"
    }
}));

const tourTypes = [
    { code: "WHL01", label: "WHL01" },
    { code: "RET01", label: "RET01" },
    { code: "WHL06", label: "WHL06" },
    { code: "RET02", label: "RET02" },
    { code: "WHL03", label: "WHL03" },
    { code: "GJ", label: "GJ" },
    { code: "EX01", label: "EX01" },
    { code: "EX02", label: "EX02" },
    { code: "EX03", label: "EX03" },
    { code: "EX04", label: "EX04" },
    { code: "EX05", label: "EX05" },
    { code: "EXGJ", label: "EXGJ" },
    { code: "EXGJ2", label: "EXGJ2" },
    { code: "EXANCORA", label: "EXANCORA" },
];


export function EventComponent(props) {
    const classes = useStyles();

    const [events, setEvents] = React.useState(props.events || []);
    const [selectedEventId, setSelectedEventId] = React.useState("");
    const [openVtDialog, setOpenVtDialog] = useState(false);
    const [tourType, setTourType] = useState(null);
    const [savingTourType, setSavingTourType] = useState(false);
    const { getAccessToken } = useAuth();
    const [value, setValue] = React.useState(0);

    const [state, setState] = React.useState({
        openSuccess: false,
        openError: false,
        vertical: 'top',
        horizontal: 'right',
        successMessage: '',
        errorMessage: ''
    });
    const {vertical, horizontal, openSuccess, successMessage, openError, errorMessage} = state;

    const handleClose = () => {
        setState({...state, openSuccess: false, openError: false});
    };

    const selectedEvent = React.useMemo(() => {
        return events.find(e => e.id === selectedEventId) || null;
    }, [events, selectedEventId]);

    const displayTypes = React.useMemo(() => {
        const map = new Map();

        events.forEach((event) => {
            event.displayTypes?.forEach((dt) => {
                map.set(String(dt.id), dt);
            });
        });

        return Array.from(map.values());
    }, [events]);

    const handleSelectChange = (e) => {
        setSelectedEventId(e.target.value);
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    const handleClear = () => {
        setSelectedEventId("");
        setValue(0);
    };

    const openVirtualTourLink = (bool) => {
        setOpenVtDialog(bool);
        if (!bool) {
            setTourType(null);
        }
    };

    async function linkVT() {
        setSavingTourType(true);
        try {
            const accessToken = await getAccessToken();
            await authorizedFetch(
                process.env.REACT_APP_API_ENDPOINT + "virtualtour/generate",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({[tourType]: selectedEventId})
                },
                accessToken
            );
            setState({
                openSuccess: true,
                openError: false,
                successMessage: "Event Updated!",
            });
        } catch (err) {
            setState({
                openSuccess: false,
                openError: true,
                errorMessage: "Something went wrong!",
            });
        } finally {
            setSavingTourType(false);
            setOpenVtDialog(false);
        }
    }

    const handleSaveSuccess = (savedEvent) => {
        setEvents((prev) => {
            const exists = prev.some(e => e.id === savedEvent.id);

            if (exists) {
                return prev.map(e =>
                    e.id === savedEvent.id ? savedEvent : e
                );
            }

            return [...prev, savedEvent];
        });

        props.onEventSaved?.(savedEvent);

        setSelectedEventId(savedEvent.id);
    };


    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <div className={classes.root}>
            <Grid item xs={12}>
                <h3 className='gucci_text' style={{margin: 0}}>
                    Manage Events
                </h3>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                    }}
                >
                    <FormControl sx={{minWidth: 400}}>
                        <InputLabel id="select-event-label">Event List</InputLabel>
                        <Select
                            className='gucci_text event_select_filter'
                            id="select-event"
                            value={selectedEventId}
                            aria-placeholder={'Select event to edit...'}
                            onChange={handleSelectChange}
                            input={<Input/>}
                            displayEmpty
                        >
                            {events.length > 0 ? (
                                events.map((event) => (
                                    <MenuItem
                                        key={event.id}
                                        value={event.id}
                                    >
                                        {event.name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="">
                                    No events
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <div style={{gap: '1rem', display: 'flex'}}>
                        {selectedEventId && (
                            <Button
                                variant="contained"
                                startIcon={<AddLink sx={{marginRight: '0.5rem'}}/>}
                                onClick={() => openVirtualTourLink(true)}
                            >
                                Update Virtual Tour
                            </Button>
                        )}
                        {selectedEventId && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon sx={{marginRight: '0.5rem'}}/>}
                                onClick={handleClear}
                            >
                                Create Event
                            </Button>
                        )}
                    </div>
                </Box>
            </Grid>
            {selectedEventId && (
                <Tabs
                    value={value}
                    onChange={handleChange}
                    className={'mt-10 mb-10'}
                    aria-label="wrapped label tabs">
                    <Tab {...a11yProps(0)} label="Event Detail"/>
                    <Tab {...a11yProps(1)} label="Looks"/>
                </Tabs>
            )}
            {!selectedEventId && (
                <Tabs value={0}>
                    <Tab label="Create Event"/>
                </Tabs>
            )}
            <CustomTabPanel className={'card'} value={value} index={0}>
                {selectedEvent ? <h3 className='gucci_text'>Event Detail</h3> :
                    <h3 className='gucci_text'>Create Event</h3>}

                <EventForm
                    selectedEvent={selectedEvent}
                    displayTypes={displayTypes}
                    onSuccess={handleSaveSuccess}
                />
            </CustomTabPanel>
            <CustomTabPanel className={'card'} value={value} index={1}>
                <h3 className='gucci_text'>Looks</h3>
                <LooksForm
                    selectedEvent={selectedEvent}>
                </LooksForm>
            </CustomTabPanel>

            <Dialog
                open={openVtDialog}
                onClose={() => openVirtualTourLink(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth maxWidth="sm"
            >
                <DialogTitle id="alert-dialog-title">
                    <Typography className="gucci_text_bold" variant="h5">Update {selectedEvent?.name} to Virtual Tour code</Typography>
                </DialogTitle>
                <DialogContent style={{padding: "20px 24px"}}>
                    <FormControl fullWidth>
                        <InputLabel id="tour-type-label">Tour Type</InputLabel>

                        <Select
                            className='gucci_text event_select_filter'
                            labelId="tour-type-label"
                            value={tourType}
                            label="Tour Type"
                            onChange={(event) => setTourType(event.target.value)}
                        >
                            {tourTypes.map(({ code, label }) => (
                                <MenuItem key={code} value={code}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => openVirtualTourLink(false)} disabled={savingTourType}
                            className="black template_button">Cancel</Button>
                    <Button onClick={() => linkVT(tourType)} disabled={savingTourType}
                            className="red template_button" autoFocus>Update</Button>
                </DialogActions>
            </Dialog>

            <Backdrop
                open={savingTourType}
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1}}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={openSuccess}
                autoHideDuration={3000}
                onClose={handleClose}
                message=""
                key={vertical + horizontal}
            >
                <Alert onClose={handleClose} severity="success" dx={{width: '300px'}}>
                    {successMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={openError}
                onClose={handleClose}
                autoHideDuration={3000}
                message=""
                key={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Alert onClose={handleClose} severity="error" dx={{width: '300px'}}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
